import { CheckoutUpsellImageAspectRatio } from "@redotech/cockroachdb-codegen/congodb/checkout-upsell-image-aspect-ratio";
import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  CheckoutImageBlockJustification,
  CheckoutOptimizationWidgetType,
  CheckoutReviewsProvider,
  lowProfileManualProductSourceSchema,
  ShippingOptionRenderTarget,
  ThresholdManualProductSourceSchema,
  UpsellProductDiscountSchema,
} from "./widget";

const baseWidgetSchema = z.object({
  /**
   * Defined `id` means the widget is existing and needs to be updated.
   * Undefined `id` means the widget is new and needs to be created
   */
  id: zExt.objectId().optional(),
  name: z.string(),
  /**
   * @deprecated Widget existence in collection indicates it's enabled
   */
  enabled: z.boolean().optional(),
});

const baseStandardWidgetSchema = baseWidgetSchema.extend({
  /**
   * When true, this widget applies to all checkouts by default (not tied to a specific checkout experience)
   */
  defaultEnabled: z.boolean().optional(),
  // Free-form render target. Known/default values come from
  // `PurchaseCheckoutBlockRenderTarget`, but merchants can place widgets at
  // arbitrary string targets (e.g. "block1").
  target: z.string(),
});

// ── Per-type builder leaf schemas ─────────────────────────────────────────
// Named so they can be composed two ways without duplication: the keyed
// `checkoutWidgetsSchema` (RPC/UI contract) and the derived discriminated
// `checkoutWidgetConfigSchema` (MCP tool contract).

/**
 * Builder-side mirror of `CheckoutUpsellStylingSchema` with required booleans,
 * so the settings UI never has to handle `null`.
 */
const checkoutUpsellStylingBuilderSchema = z.object({
  imageAspectRatio: createEnum(CheckoutUpsellImageAspectRatio).optional(),
  squareImageBackdropCorners: z.boolean(),
  squareButtonCorners: z.boolean(),
  plainProductTitle: z.boolean(),
  hideZeroMinorUnits: z.boolean(),
});
export type CheckoutUpsellStylingBuilder = z.infer<
  typeof checkoutUpsellStylingBuilderSchema
>;

/**
 * `Square` matches the `s-image` default of `1/1`, so this reproduces the
 * historical rendering exactly.
 */
export const DEFAULT_CHECKOUT_UPSELL_STYLING: CheckoutUpsellStylingBuilder = {
  imageAspectRatio: CheckoutUpsellImageAspectRatio.Square,
  squareImageBackdropCorners: false,
  squareButtonCorners: false,
  plainProductTitle: false,
  hideZeroMinorUnits: false,
};

const thresholdUpsellBuilderSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.THRESHOLD_UPSELL),
  thresholdAmount: z.number(),
  currency: createEnum(Currency),
  gteThresholdText: z.string(),
  ltThresholdText: z.string(),
  addToCartButtonText: z.string(),
  addToCartButtonColor: z.string(),
  addToCartButtonTextColor: z.string(),
  progressBarColor: z.string(),
  excludedProductTags: z.array(z.string()),
  fallbackProductTags: z.array(z.string()),
  manualProductSources: z.array(ThresholdManualProductSourceSchema),
  defaultProductDiscount: UpsellProductDiscountSchema.optional(),
  styling: checkoutUpsellStylingBuilderSchema.optional(),
});

const lowProfileProductUpsellBuilderSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL),
  addToCartButtonColor: z.string(),
  addToCartButtonTextColor: z.string(),
  excludedProductTags: z.array(z.string()),
  fallbackProductTags: z.array(z.string()),
  manualProductSources: z.array(lowProfileManualProductSourceSchema),
});

const fulfillmentCutoffBuilderSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF),
  text: z.string(),
  backgroundColor: z.string(),
  iconColor: z.string(),
});

const contentBlockBuilderSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.CONTENT_BLOCK),
  sections: z.array(
    z.object({
      _id: z.string(),
      title: z.string().nullish(),
      description: z.string().nullish(),
      imageUrl: z.string().nullish(),
    }),
  ),
});

const imageBlockBuilderSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.IMAGE_BLOCK),
  imageUrl: z.string().nullish(),
  imageWidthPx: z.coerce.string(),
  imageJustification: createEnum(CheckoutImageBlockJustification),
});

const shippingOptionContentBuilderSchema = baseWidgetSchema.extend({
  target: createEnum(ShippingOptionRenderTarget),
  type: z.literal(CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT),
  title: z.string(),
  description: z.string(),
  imageUrl: z.string().nullish(),
  backgroundColor: z.string(),
});

const shippingOptionImageBuilderSchema = baseWidgetSchema.extend({
  target: createEnum(ShippingOptionRenderTarget),
  type: z.literal(CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE),
  imageUrl: z.string().nullish(),
  imageWidth: z.string(),
  imageJustification: createEnum(CheckoutImageBlockJustification),
});

const shippingOptionProductUpsellBuilderSchema = baseWidgetSchema.extend({
  target: createEnum(ShippingOptionRenderTarget),
  type: z.literal(
    CheckoutOptimizationWidgetType.SHIPPING_OPTION_PRODUCT_UPSELL,
  ),
  addToCartButtonText: z.string(),
  excludedProductTags: z.array(z.string()),
  fallbackProductTags: z.array(z.string()),
  manualProductSources: z.array(lowProfileManualProductSourceSchema),
  defaultProductDiscount: UpsellProductDiscountSchema.optional(),
  // Optional threshold progress bar. Absent (`null`) means the merchant
  // has the progress bar turned off; present means it renders above the
  // product list.
  progressBar: z
    .object({
      thresholdAmount: z.number(),
      currency: createEnum(Currency),
      gteThresholdText: z.string(),
      ltThresholdText: z.string(),
    })
    .nullish(),
});

const failedDiscountRescueBuilderSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE),
  discountId: zExt.objectId().optional(),
  text: z.string(),
  buttonText: z.string(),
});

const productReviewsBuilderSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.PRODUCT_REVIEWS),
  provider: createEnum(CheckoutReviewsProvider),
  minRating: z.number().int().min(1).max(5),
  display: z.object({
    showProductName: z.boolean(),
    showReviewTitle: z.boolean(),
    showReviewBody: z.boolean(),
    showReviewerName: z.boolean(),
    showVerifiedBadge: z.boolean(),
  }),
});

/**
 * Keyed-map shape: an object with one optional entry per widget type. This is
 * the contract the merchant RPC (create/update checkout widget) and builder UI
 * use. The populated entry indicates the widget being created/edited.
 */
export const checkoutWidgetsSchema = z.object({
  [CheckoutOptimizationWidgetType.THRESHOLD_UPSELL]:
    thresholdUpsellBuilderSchema.optional(),
  [CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL]:
    lowProfileProductUpsellBuilderSchema.optional(),
  [CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF]:
    fulfillmentCutoffBuilderSchema.optional(),
  [CheckoutOptimizationWidgetType.CONTENT_BLOCK]:
    contentBlockBuilderSchema.optional(),
  [CheckoutOptimizationWidgetType.IMAGE_BLOCK]:
    imageBlockBuilderSchema.optional(),
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT]:
    shippingOptionContentBuilderSchema.optional(),
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE]:
    shippingOptionImageBuilderSchema.optional(),
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_PRODUCT_UPSELL]:
    shippingOptionProductUpsellBuilderSchema.optional(),
  [CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE]:
    failedDiscountRescueBuilderSchema.optional(),
  [CheckoutOptimizationWidgetType.PRODUCT_REVIEWS]:
    productReviewsBuilderSchema.optional(),
});

export type CheckoutWidgetsData = z.infer<typeof checkoutWidgetsSchema>;

/**
 * Discriminated-union shape: a single widget's builder input, discriminated on
 * `type`. Composed from the same per-type leaves as `checkoutWidgetsSchema`, so
 * the two never drift. This is the cleaner single-object contract used by the
 * MCP checkout-widget tools (`ccw__create` / `ccw__update`).
 */
export const checkoutWidgetConfigSchema = z.discriminatedUnion("type", [
  thresholdUpsellBuilderSchema,
  lowProfileProductUpsellBuilderSchema,
  fulfillmentCutoffBuilderSchema,
  contentBlockBuilderSchema,
  imageBlockBuilderSchema,
  shippingOptionContentBuilderSchema,
  shippingOptionImageBuilderSchema,
  shippingOptionProductUpsellBuilderSchema,
  failedDiscountRescueBuilderSchema,
  productReviewsBuilderSchema,
]);

export type CheckoutWidgetConfig = z.infer<typeof checkoutWidgetConfigSchema>;

/**
 * Wrap a single discriminated `config` into the keyed-map shape the RPC/service
 * layer consumes (`{ [config.type]: config }`). Used at the MCP boundary, which
 * holds a `config` but calls the keyed-map update service. A typed switch
 * (rather than a computed-key literal) so the correlated narrowing holds
 * without a cast.
 */
export function checkoutWidgetConfigToData(
  config: CheckoutWidgetConfig,
): CheckoutWidgetsData {
  switch (config.type) {
    case CheckoutOptimizationWidgetType.THRESHOLD_UPSELL:
      return { [CheckoutOptimizationWidgetType.THRESHOLD_UPSELL]: config };
    case CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL:
      return {
        [CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL]: config,
      };
    case CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF:
      return { [CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF]: config };
    case CheckoutOptimizationWidgetType.CONTENT_BLOCK:
      return { [CheckoutOptimizationWidgetType.CONTENT_BLOCK]: config };
    case CheckoutOptimizationWidgetType.IMAGE_BLOCK:
      return { [CheckoutOptimizationWidgetType.IMAGE_BLOCK]: config };
    case CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT:
      return {
        [CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT]: config,
      };
    case CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE:
      return { [CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE]: config };
    case CheckoutOptimizationWidgetType.SHIPPING_OPTION_PRODUCT_UPSELL:
      return {
        [CheckoutOptimizationWidgetType.SHIPPING_OPTION_PRODUCT_UPSELL]: config,
      };
    case CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE:
      return {
        [CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE]: config,
      };
    case CheckoutOptimizationWidgetType.PRODUCT_REVIEWS:
      return { [CheckoutOptimizationWidgetType.PRODUCT_REVIEWS]: config };
    default:
      return assertNever(config);
  }
}

/**
 * The global state store for the checkout widgets builder.
 */
const checkoutWidgetsBuilderStateSchema = z.object({
  selectedWidget: createEnum(CheckoutOptimizationWidgetType),
  checkoutWidgets: checkoutWidgetsSchema,
});
export type CheckoutWidgetsBuilderState = z.infer<
  typeof checkoutWidgetsBuilderStateSchema
>;
